import React, { useCallback, useMemo } from 'react';
import { configureIguazuSSR } from 'iguazu-holocron';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import DataManager from '@americanexpress/data-manager';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { reducer } from '../ducks';
import childRoutes from '../childRoutes';
import ErrorReporter from './error/ErrorReporter';

const appConfig = require('../appConfig').default;

export function AxpPeerPaymentsPartnerRoot(
  {
    dispatch,
    children,
  }) {
  const findPartnerName = useCallback(() => {
    const path = window.location.pathname;
    if (/venmo/.test(path)) {
      return 'Venmo';
    }
    if (/paypal/.test(path)) {
      return 'Paypal';
    }
    return '';
  }, [window.location.pathname]);

  const dynamicTitle = useMemo(() => {
    const path = window.location.pathname;
    if (/message\/success/.test(path)) {
      return 'Send & Split Enrollment';
    }
    if (/message\/enrolled_not_linked/.test(path)) {
      return 'Enrolled Not Linked to Send & Split';
    }
    if (/message\/no_email_on_file/.test(path)) {
      return 'No Email on File for Send & Split';
    }
    if (/message\/no_address/.test(path)) {
      return 'Missing Demographic Information for Send & Split';
    }
    if (/message\/system_error/.test(path)) {
      return 'System Error';
    }
    if (/message\/ineligible/.test(path)) {
      return 'Send & Split: Only Eligible for US Consumer Basic Card Members';
    }
    if (/message\/enrolled/.test(path)) {
      return 'Already Enrolled in Send & Split';
    }
    if (/\/enroll/.test(path)) {
      return `Send & Split ${findPartnerName()} How It Work`;
    }
    if (/\/send-and-split/.test(path)) {
      return 'Send & Split Enrollment';
    }
    return '';
  }, [window.location.pathname]);

  const {
    onTrackCall,
    ddlProviderProps,
    reactlyticsProps,
  } = getAmexDataManagerConfig({
    initialDDL: {
      historyLimit: 70,
    },
  });

  return (
    <React.Fragment>
      <Helmet
        {...{
          link: [
            {
              rel: 'stylesheet',
              href: 'https://www.aexp-static.com/cdaas/one/statics/axp-dls/6.8.0/package/dist/6.8.0/styles/dls.min.css',
            },
          ],
          script: [
            {
              type: 'text/javascript',
              src: 'https://www.aexp-static.com/cdaas/one/statics/axp-dls/6.1.0/package/dist/6.1.0/scripts/dls.min.js',
            },
          ],
          meta: [
            { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' },
            { charset: 'utf-8' },
            { name: 'theme-color', content: '#006FCF' },
          ],
        }}
      >
        <html lang="en" />
        <title>{dynamicTitle}</title>
      </Helmet>
      <DataManager
        onTrackCall={onTrackCall}
        ddlProviderProps={ddlProviderProps}
        reactlyticsProps={reactlyticsProps}
      >
        <AmexDDLRegister />
        <DDLRegister name="user.authenticated" value={false} />
        <Adobe version="1.0.0" />
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.2"
        />
        <SharedScript
          name="one-identity-session"
          windowCheck="AmexSession"
          version="^1.0.0"
        />
        <ErrorReporter dispatch={dispatch} />
        <div data-module-name="axp-peer-payments-partner-root">
          {children}
        </div>
      </DataManager>
    </React.Fragment>
  );
}

AxpPeerPaymentsPartnerRoot.childRoutes = childRoutes;

AxpPeerPaymentsPartnerRoot.propTypes = {
  children: PropTypes.node.isRequired,
  dispatch: PropTypes.node.isRequired,
};

/* istanbul ignore next */
if (!global.BROWSER) {
  AxpPeerPaymentsPartnerRoot.appConfig = appConfig;
}

AxpPeerPaymentsPartnerRoot.holocron = {
  name: 'axp-peer-payments-partner-root',
  reducer,
  loadModuleData: async ({ store, module, ownProps }) => {
    await configureIguazuSSR({ store, module, ownProps });
  },
};

export default oneAppModuleWrapper('axp-peer-payments-partner-root', { allowInViewDiv: true })(AxpPeerPaymentsPartnerRoot);
